import { useState } from "react";
import type { Bootstrap } from "../../shared/types";
import { formatMoney, parseMoney } from "../lib/format";

// A simple "what can we afford?" calculator built on the spare each month
// (income − outgoings). Pure client-side maths — nothing saved for MVP.
export default function PlanAhead({ data }: { data: Bootstrap | null }) {
  const spareMinor = data?.summary.leftAfterEverythingMinor ?? 0;

  const [name, setName] = useState("");
  const [cost, setCost] = useState("");
  const [saved, setSaved] = useState("0");
  const [perMonth, setPerMonth] = useState((Math.max(spareMinor, 0) / 100).toFixed(0));

  if (!data) return <div className="state">Loading…</div>;

  const costMinor = parseMoney(cost) ?? 0;
  const savedMinor = parseMoney(saved) ?? 0;
  const perMonthMinor = parseMoney(perMonth) ?? 0;
  const stillToSave = Math.max(0, costMinor - savedMinor);
  const months = stillToSave === 0 ? 0 : perMonthMinor > 0 ? Math.ceil(stillToSave / perMonthMinor) : null;

  return (
    <div className="page">
      <p className="page__eyebrow">Plan ahead</p>
      <h1 className="page__title">What can we afford?</h1>
      <p className="page__lede">
        You have about <strong>{formatMoney(Math.max(spareMinor, 0))}</strong> spare each month after bills.
      </p>

      <section className="panel">
        <div className="form">
          <div className="form__row">
            <label className="field">
              <span className="field__label">What are you buying?</span>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Family holiday" />
            </label>
            <label className="field">
              <span className="field__label">Total cost (£)</span>
              <input className="input" inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="2500" />
            </label>
          </div>
          <div className="form__row">
            <label className="field">
              <span className="field__label">Already saved (£)</span>
              <input className="input" inputMode="decimal" value={saved} onChange={(e) => setSaved(e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Put aside / month (£)</span>
              <input className="input" inputMode="decimal" value={perMonth} onChange={(e) => setPerMonth(e.target.value)} />
            </label>
          </div>
        </div>

        <div className="plan-result">
          <div>
            <p className="stat-card__label">Time to save up</p>
            <p className="plan-result__big">
              {months === 0 ? "Already there" : months == null ? "—" : months === 1 ? "1 month" : `${months} months`}
            </p>
          </div>
          <div>
            <p className="stat-card__label">Still to save</p>
            <p className="plan-result__big">{formatMoney(stillToSave)}</p>
            <p className="stat-card__sub">of {formatMoney(costMinor)} total</p>
          </div>
          <div>
            <p className="stat-card__label">Monthly set-aside</p>
            <p className="plan-result__big">{formatMoney(perMonthMinor)}</p>
            {perMonthMinor > spareMinor && spareMinor >= 0 && (
              <p className="stat-card__sub stat-card__value--negative">More than your spare</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
